import { useEffect, useRef, useState } from 'react';
import { Mic, Square } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Speak instead of typing a remark (operator, 28-10). Uses the browser's own speech recognition —
 * Chrome / Edge on the portal's HTTPS, nothing sent to our server and nothing to pay for. The words
 * are ADDED to whatever is already in the box, so the operator can still read and correct them
 * before pressing Submit. English (India) or Hindi; the choice is remembered on this browser.
 */

const LANGS = [
  { code: 'en-IN', label: 'EN' },
  { code: 'hi-IN', label: 'हि' },
] as const;
const LANG_KEY = 'mic-lang';

function recognitionCtor(): any {
  if (typeof window === 'undefined') return null;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null;
}

/** `prev` + one space + `add` — never a double space, never a leading one. */
export function appendSpoken(prev: string | null | undefined, add: string): string {
  const a = String(add ?? '').trim();
  if (!a) return String(prev ?? '');
  const p = String(prev ?? '').replace(/\s+$/, '');
  return p ? `${p} ${a}` : a;
}

export function MicButton({ onText, className = '', size = 14 }: { onText: (text: string) => void; className?: string; size?: number }) {
  const Ctor = recognitionCtor();
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [lang, setLang] = useState<string>(() => {
    try { return localStorage.getItem(LANG_KEY) || 'en-IN'; } catch { return 'en-IN'; }
  });
  const rec = useRef<any>(null);

  useEffect(() => () => { try { rec.current?.abort(); } catch { /* already stopped */ } }, []);

  const toggleLang = () => {
    const next = lang === 'en-IN' ? 'hi-IN' : 'en-IN';
    setLang(next);
    try { localStorage.setItem(LANG_KEY, next); } catch { /* private window — fine for this session */ }
  };

  const start = () => {
    if (!Ctor) { toast.error('Voice input works in Chrome or Edge.'); return; }
    if (listening) { try { rec.current?.stop(); } catch { /* ignore */ } return; }
    const r = new Ctor();
    r.lang = lang;
    r.interimResults = true;
    r.continuous = false;
    r.maxAlternatives = 1;
    r.onresult = (e: any) => {
      let finalText = '';
      let partial = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0]?.transcript ?? '';
        if (e.results[i].isFinal) finalText += t; else partial += t;
      }
      setInterim(partial);
      if (finalText.trim()) onText(finalText.trim());
    };
    r.onerror = (e: any) => {
      const code = e?.error;
      if (code === 'not-allowed' || code === 'service-not-allowed') toast.error('Allow microphone access for this site to use voice input.');
      else if (code === 'no-speech') toast.message('Did not hear anything — tap the mic and speak again.');
      else if (code !== 'aborted') toast.error(`Voice input stopped (${code ?? 'error'}).`);
    };
    r.onend = () => { setListening(false); setInterim(''); };
    rec.current = r;
    try { r.start(); setListening(true); } catch { setListening(false); }
  };

  return (
    <span className={`inline-flex items-center gap-1 shrink-0 ${className}`}>
      <button type="button" onClick={start} disabled={!Ctor}
        title={!Ctor ? 'Voice input works in Chrome or Edge' : listening ? 'Listening… tap to stop' : `Speak a remark (${lang === 'hi-IN' ? 'Hindi' : 'English'})`}
        aria-label={listening ? 'Stop voice input' : 'Start voice input'}
        data-testid="mic-button"
        className={`h-8 w-8 rounded-full flex items-center justify-center border transition ${listening ? 'bg-rose-600 border-rose-600 text-white animate-pulse' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'} disabled:opacity-40 disabled:cursor-not-allowed`}>
        {listening ? <Square size={size - 2} /> : <Mic size={size} />}
      </button>
      <button type="button" onClick={toggleLang} disabled={listening}
        title="Voice input language — tap to switch English / Hindi"
        className="text-[10px] font-semibold text-slate-500 hover:text-slate-800 px-1 disabled:opacity-40">
        {LANGS.find((l) => l.code === lang)?.label ?? 'EN'}
      </button>
      {listening && interim && <span className="text-[11px] text-slate-400 italic max-w-[180px] truncate">{interim}</span>}
    </span>
  );
}
